import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { FolderKanban, LayoutTemplate } from "lucide-react";
import { defineModuleUi, from, unwrap, useOrg } from "@vixio/data";
import { formatInt, registerAuditLabels, lazyPage } from "@vixio/ui";
import "./api";
import type { ProjectRow } from "./api";

export * from "./api";

// Telas carregadas sob demanda: cada uma vira um arquivo à parte no build.
const ProjectsPage = lazyPage(() => import("./ProjectsPage"), "ProjectsPage");
const ProjectPage = lazyPage(() => import("./ProjectPage"), "ProjectPage");
const TemplatesPage = lazyPage(() => import("./TemplatesPage"), "TemplatesPage");

registerAuditLabels({
  "delivery.project_created": "Projeto aberto",
  "delivery.project_updated": "Projeto alterado",
  "delivery.project_status": "Situação do projeto alterada",
  "delivery.deliverable_upserted": "Entregável salvo",
  "delivery.deliverable_submitted": "Entregável enviado ao cliente",
  "delivery.deliverable_accepted": "Entregável aceito",
  "delivery.deliverable_rejected": "Entregável recusado",
  "delivery.time_reversed": "Horas estornadas",
  "delivery.risk_upserted": "Risco do projeto salvo",
  "delivery.template_upserted": "Modelo de serviço salvo",
  "delivery.role_rate_set": "Custo por papel alterado",
  "delivery.settings_set": "Parâmetros de entrega alterados",
});

function DeliveryWidget() {
  const org = useOrg();
  const q = useQuery({
    queryKey: ["delivery", "widget", org.id],
    queryFn: () =>
      unwrap<Array<Pick<ProjectRow, "id" | "title" | "hours_pct" | "deliverables_submitted" | "late">>>(
        from("delivery", "v_projects").select("id, title, hours_pct, deliverables_submitted, late").eq("org_id", org.id).in("status", ["active", "planning"]).limit(500),
      ),
  });
  const rows = q.data ?? [];
  const over = rows.filter((r) => (r.hours_pct ?? 0) > 100);
  return (
    <div className="vx-stack">
      <dl className="vx-dl">
        <dt>Em andamento</dt>
        <dd className="num">{formatInt(rows.length)}</dd>
        <dt>Aguardando aceite</dt>
        <dd className="num">{formatInt(rows.reduce((a, r) => a + Number(r.deliverables_submitted), 0))}</dd>
        <dt>Acima do teto</dt>
        <dd className={over.length ? "num text-crit" : "num"}>{formatInt(over.length)}</dd>
      </dl>
      {over.slice(0, 3).map((r) => (
        <Link key={r.id} to={`/entrega/projetos/${r.id}?aba=horas`}>
          {r.title}
        </Link>
      ))}
      <Link to="/entrega/projetos">Abrir projetos</Link>
    </div>
  );
}

export default defineModuleUi({
  key: "delivery",
  title: "Projetos e entregas",
  domain: "delivery",
  icon: FolderKanban,
  menu: [
    { path: "/entrega/projetos", label: "Projetos", minRole: "member", icon: FolderKanban, order: 10, keywords: "entregável aceite horas teto cronograma fase tarefa risco marco" },
    { path: "/entrega/modelos", label: "Modelos de serviço", minRole: "member", icon: LayoutTemplate, order: 15, keywords: "retainer diagnóstico dose sistemas inpes custo por papel" },
  ],
  routes: [
    { path: "/entrega/projetos", element: <ProjectsPage />, minRole: "member" },
    { path: "/entrega/projetos/:id", element: <ProjectPage />, minRole: "member" },
    { path: "/entrega/modelos", element: <TemplatesPage />, minRole: "member" },
  ],
  widgets: [{ key: "delivery.projects", title: "Entregas", minRole: "member", span: 1, order: 34, element: DeliveryWidget }],
});
